import { ComponentRef, Directive, inject, input, InputSignal, OnChanges, Type, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { GroupItemModel, isExternalComponentItem, isGroupItem, isInputItem, ItemBaseModel, ItemType } from '@libs/form/models';
import {
  DYNAMIC_ELEMENTS_MAP,
  DYNAMIC_ELEMENTS_MAP_EXTERNAL,
  DynamicElementsMap,
  GroupBaseComponent,
  InputBaseComponent,
  ItemBaseComponent,
} from '../../models';

@Directive({
  selector: '[uiFormDynamicElements]',
})
export class DynamicElementsDirective implements OnChanges {
  private readonly itemsMap: DynamicElementsMap = {
    ...inject(DYNAMIC_ELEMENTS_MAP),
    ...(inject(DYNAMIC_ELEMENTS_MAP_EXTERNAL, { optional: true }) ?? {}),
  };

  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);

  public groupControl: InputSignal<FormGroup> = input.required();
  public groupItem: InputSignal<GroupItemModel> = input.required();

  public ngOnChanges(): void {
    this.viewContainerRef.clear();

    Object.keys(this.groupControl().controls).forEach(
      key => void this.groupControl().removeControl(key),
    );

    this.groupItem().items.forEach(
      this.createComponent.bind(this),
    );
  }

  private createComponent(item: ItemBaseModel): void {
    const lookupKey: string = isExternalComponentItem(item) ? item.componentId : item.type;
    const type: Type<ItemBaseComponent> | undefined = this.itemsMap[lookupKey as ItemType];

    if (!type) {
      throw new Error(
        `Form component for item type '${lookupKey}' is not defined`,
      );
    }

    const componentRef: ComponentRef<ItemBaseComponent> = this.viewContainerRef.createComponent(type);
    const component: ItemBaseComponent = componentRef.instance;

    this.setInput(componentRef, item);
    this.addControl(component, item);
  }

  private setInput(componentRef: ComponentRef<ItemBaseComponent>, item: ItemBaseModel): void {
    componentRef.setInput('data', item);
  }

  private addControl(component: ItemBaseComponent, item: ItemBaseModel): void {
    if (isGroupItem(item)) {
      const groupComponent: GroupBaseComponent = component as GroupBaseComponent;

      groupComponent.control = new FormGroup({});
      this.groupControl().setControl(item.name, groupComponent.control);

      return;
    }

    if (isInputItem(item)) {
      const inputComponent: InputBaseComponent = component as InputBaseComponent;

      inputComponent.control = new FormControl();
      this.groupControl().setControl(item.name, inputComponent.control);

      return;
    }
  }
}
